import { createRoute } from '@tanstack/react-router';
import { T, msg } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { HeaderPageTitle } from 'src/context/header-title.context';
import {
  Route as pathlessAuthenticatedRoute,
  requireFeatureFlag,
  requirePermission,
} from '../../../route';
import { SettingsOrganizationLayoutPage } from '../SettingsOrganizationPage';
import { TaskTypeEditorBody } from './TaskTypeEditorBody';
import { useTaskTypeCreateEditor } from './useTaskTypeCreateEditor';

/**
 * New task type — the full editor (identity, behavior, stages, actions) as a
 * page, mirroring the detail view. Everything is held locally and persisted
 * with a single create; on success we return to the task types list.
 */
function TaskTypeCreatePage() {
  return (
    <SettingsOrganizationLayoutPage hideRail>
      <TaskTypeCreateContent />
    </SettingsOrganizationLayoutPage>
  );
}

function TaskTypeCreateContent() {
  const {
    canManage,
    editor,
    canSave,
    isDirty,
    setDraft,
    handleNameChange,
    stageOptions,
    unsavedGuard,
    handleBack,
    handleCreate,
    isCreating,
  } = useTaskTypeCreateEditor();

  // fallow-ignore-next-line code-duplication
  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-10 bg-background flex flex-wrap items-start justify-between gap-3 px-2 py-2">
        <div className="min-w-0 flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 w-fit px-2 text-muted-foreground"
            onClick={handleBack}
          >
            <ArrowLeftIcon className="mr-1.5 h-4 w-4" />
            <T context="task type detail back button">Back to task types</T>
          </Button>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <HeaderPageTitle>
              {editor.name.trim() || (
                <T context="page title while creating a task type">New</T>
              )}
            </HeaderPageTitle>
            {editor.key.trim() ? (
              <Badge variant="outline" className="font-mono font-normal">
                {editor.key}
              </Badge>
            ) : null}
            {isDirty ? (
              <Badge variant="secondary">
                <T context="badge when the task type editor has unsaved edits">
                  Unsaved
                </T>
              </Badge>
            ) : null}
          </div>
        </div>
        {canManage && (
          <Button
            size="sm"
            disabled={!canSave || isCreating}
            onClick={() => void handleCreate()}
          >
            {isCreating ? (
              <T context="button label while creating a task type">Creating…</T>
            ) : (
              <T context="button, create the task type">Create</T>
            )}
          </Button>
        )}
      </header>

      <TaskTypeEditorBody
        editor={editor}
        setDraft={setDraft}
        canManage={canManage}
        stageOptions={stageOptions}
        keyDisabled={false}
        onNameChange={handleNameChange}
        unsavedGuard={unsavedGuard}
      />
    </div>
  );
}

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization/task-types/new',
  staticData: {
    app: {
      access: {
        featureFlag: 'tasks-enabled',
        permissionsAnyOf: [
          { action: Action.Create, subject: Subject.TaskType },
        ],
      },
    },
  },
  component: TaskTypeCreatePage,
  loader: () => ({
    crumb: msg('New', { context: 'page breadcrumb, creating a task type' }),
    crumbAncestors: [
      {
        title: msg('Organization Settings', { context: 'page breadcrumb' }),
        path: '/settings/organization',
      },
      {
        title: msg('Task Type', { context: 'page breadcrumb' }),
        path: '/settings/organization/task-types',
      },
    ],
  }),
  beforeLoad: async ({ context }) => {
    requireFeatureFlag('tasks-enabled');
    await requirePermission({
      context,
      action: Action.Create,
      subject: Subject.TaskType,
    });
  },
});
